import '@css/Map.css'
import 'mapbox-gl/dist/mapbox-gl.css'

import boatOutlineImg from '@assets/images/mekkah_icons/boat_outline.svg'
import Clock from '@assets/images/mekkah_icons/CLock.svg'
import { drawCompass, drawMekkah } from '@components/Maps/Mapbox/mapbox_helpers'
import MapboxPolyline from '@components/Maps/Mapbox/MapboxPolyline'
import MapboxWrapper from '@components/Maps/Mapbox/MapboxWrapper'
import YisToolTip from '@components/material/YisTooltip'
import MekkahView from '@components/Mecca/MekkahView'
import PrayerTimes from '@components/Mecca/PrayerTimes'
import MainWrapper from '@components/Utils/MainWrapper'
import type { AppState } from '@constants/types'
import { setYachtTransformation } from '@constants/utils'
import useApi from '@hooks/useApi'
import useSocket from '@hooks/useSocket'
import { Typography } from '@mui/material'
import Grid from '@mui/material/Grid'
import { styled } from '@mui/material/styles'
import { allDistanceUnits, type DistanceUnit } from '@shared_types/unit_types'
import type { Metric } from '@shared_types/vessel_data'
import type { MekkaDirection } from '@shared_types/yis_types'
import { update } from '@store/actions'
import { convertDistance, getDistanceUnit } from '@utils/unit_utils'
import { isNumber } from 'lodash'
import type { LngLatLike, Map, Marker } from 'mapbox-gl'
import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

const WrapperContainer = styled(MainWrapper)({
  minHeight: '45vh'
})

const DistanceUnitText = styled(Typography, {
  shouldForwardProp: prop => prop !== 'isActive'
})<{ isActive: boolean }>(({ isActive }) => ({
  paddingLeft: '2rem',
  cursor: 'pointer',
  opacity: isActive ? 1 : 0.4,
  transition: 'opacity .3s ease'
}))

const IconContainer = styled(Grid)({
  position: 'absolute',
  right: -65,
  top: '1.25rem',
  cursor: 'pointer',
  zIndex: 1000
})

const Icon = styled('img')({
  width: '2.2rem',
  height: '2.2rem',
  margin: 0,
  filter: 'var(--logo-light)'
})

const MapWrapper = styled(Grid)({
  position: 'relative',
  width: '100%',
  maxWidth: '100%',
  flexBasis: '100%',
  minWidth: 0,
  height: '55vh'
})

const MapContainer = styled(Grid)({
  position: 'relative',
  width: '100%',
  height: '100%',
  minWidth: 0
})

export const mekkaLocation: LngLatLike = [39.826168, 21.42251]

export default function MekkahPage () {
  const { apiGet } = useApi()
  const mapInstance = useRef<Map | null>(null)
  const compassMarker = useRef<Marker | null>(null)
  const position = useSelector((state: AppState) => state.main)
  const dispatch = useDispatch()

  const [distanceUnit, setDistanceUnit] = useState<DistanceUnit>(getDistanceUnit())
  const [mekkahOffset, setMekkahOffset] = useState<MekkaDirection | null>(null)
  const [showPrayerTimes, setShowPrayerTimes] = useState(false)

  useSocket<Metric>('main.heading', heading => {
    if (isNumber(heading.value) && !isNaN(heading.value)) {
      dispatch(update('heading', { heading }))
    }
  })

  useEffect(() => {
    apiGet<MekkaDirection>('api/mecca/location').then(data => {
      if (data) {
        setMekkahOffset(data)
      }
    })
  }, [])

  useEffect(() => {
    localStorage.setItem('distanceUnit', distanceUnit)
  }, [distanceUnit])

  const onMapZoom = (map: Map, zoom: number) => {
    const shouldDrawCompass = zoom >= 6

    if (shouldDrawCompass && !compassMarker.current) {
      compassMarker.current = drawCompass(map, position.position.value)
    } else if (!shouldDrawCompass && compassMarker.current) {
      compassMarker.current.remove()
      compassMarker.current = null
    }
  }

  const onMapLoaded = (map: Map) => {
    mapInstance.current = map
    setYachtTransformation(map, position.heading.value, boatOutlineImg)
    onMapZoom(map, map.getZoom())
    drawMekkah(map)
  }

  return (
    <Grid container>
      <WrapperContainer withPushBack={true} title="Makkah">
        <Grid container>
          {mekkahOffset && <Grid container style={{ position: 'relative' }} sx={{
            alignItems: 'center'
          }}>
            <Typography variant="h1">{convertDistance(mekkahOffset.distance * 0.001, distanceUnit)}</Typography>

            <Grid>
              {allDistanceUnits.map(unit => <DistanceUnitText
                key={unit}
                isActive={unit === distanceUnit}
                onClick={() => setDistanceUnit(unit)}
                variant="h3"
              >
                {unit}
              </DistanceUnitText>)}
            </Grid>

            <YisToolTip text={'Prayer times'}>
              <IconContainer onClick={() => setShowPrayerTimes(true)}>
                <Icon src={Clock} alt="clock" />
              </IconContainer>
            </YisToolTip>
          </Grid>}
        </Grid>
      </WrapperContainer>

      <MapWrapper container size={12}>
        <MapContainer size={12}>
          <MapboxWrapper
            mode="mapbox2d"
            center={position.position.value}
            zoom={7}
            onZoom={onMapZoom}
            onLoaded={onMapLoaded}
            polylines={[MapboxPolyline.arc(position.position.value, mekkaLocation, 50)]}
            fullHeight={true}
            fullWidth={true}
            showYachtOutline
            showRoute
          />
          <MekkahView />
        </MapContainer>
        {showPrayerTimes && <PrayerTimes close={() => setShowPrayerTimes(false)} />}
      </MapWrapper>
    </Grid>
  )
}
